import React, { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import UrduKeyboard from '../../Components/UrduKeyboard';

function SupplierKhata({
  company,
  companyLanguage,
  ledgerParties,
  supplierSearchFilter,
  setSupplierSearchFilter,
  showSupplierForm,
  setShowSupplierForm,
  ledgerPartyForm,
  handleLedgerPartyFormChange,
  handleCreateLedgerParty,
  editingPartyId,
  setEditingPartyId,
  isLoadingLedger,
  selectedLedgerParty,
  fetchLedgerTransactions,
  handleEditLedgerParty,
  handleDeleteLedgerParty,
  ledgerTransactions,
  ledgerTransactionForm,
  handleLedgerTransactionFormChange,
  handleCreateLedgerTransaction,
  activeTab,
  activeLedgerTab,
  setLedgerPartyForm,
  bills = []
}) {
  const formRef = useRef(null);
  const printRef = useRef(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showUrduKeyboard, setShowUrduKeyboard] = useState(false);
  const [showSearchUrduKeyboard, setShowSearchUrduKeyboard] = useState(false);
  const urduNameInputRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle Urdu keyboard input for name field
  const handleUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setLedgerPartyForm((prev) => ({
        ...prev,
        nameUrdu: prev.nameUrdu ? prev.nameUrdu.slice(0, -1) : ''
      }));
    } else {
      setLedgerPartyForm((prev) => ({
        ...prev,
        nameUrdu: (prev.nameUrdu || '') + key
      }));
    }
  };

  // Handle Urdu keyboard input for search field
  const handleSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setSupplierSearchFilter((prev) => prev ? prev.slice(0, -1) : '');
    } else {
      setSupplierSearchFilter((prev) => (prev || '') + key);
    }
  };

  // Toggle Urdu keyboard on search input click
  // Open keyboard if field is empty, otherwise toggle
  const handleSearchInputClick = () => {
    if (!supplierSearchFilter || supplierSearchFilter.trim() === '') {
      // If field is empty, open keyboard
      setShowSearchUrduKeyboard(true);
    } else {
      // Otherwise toggle
      setShowSearchUrduKeyboard(prev => !prev);
    }
  };

  // Auto-scroll to form when it opens
  useEffect(() => {
    if (showSupplierForm && formRef.current) {
      // Small delay to ensure form is rendered
      setTimeout(() => {
        formRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
        // Also focus on first input field
        const firstInput = formRef.current?.querySelector('input[name="name"]');
        if (firstInput) {
          setTimeout(() => {
            firstInput.focus();
          }, 300);
        }
      }, 100);
    }
  }, [showSupplierForm]);

  // Auto-close Urdu keyboard when English character is typed in nameUrdu field
  // Auto-open when field becomes empty
  useEffect(() => {
    if (ledgerPartyForm.nameUrdu === undefined || ledgerPartyForm.nameUrdu === null) return;

    const value = String(ledgerPartyForm.nameUrdu);

    // Check if field contains any English characters
    const hasEnglishChar = /[a-zA-Z]/.test(value);

    if (hasEnglishChar) {
      // Close Urdu keyboard if English character is found
      setShowUrduKeyboard(false);
    } else if (value.trim() === '' && showUrduKeyboard === false) {
      // Open Urdu keyboard if field becomes empty and keyboard is not already open
      // Only open if the field is focused (to avoid opening when form is reset)
      if (urduNameInputRef.current && urduNameInputRef.current === document.activeElement) {
        setShowUrduKeyboard(true);
      }
    }
  }, [ledgerPartyForm.nameUrdu, showUrduKeyboard]);

  // Auto-close Urdu keyboard when English character is typed in search field
  // Auto-open when search field becomes empty
  useEffect(() => {
    if (supplierSearchFilter === undefined || supplierSearchFilter === null) return;

    const value = String(supplierSearchFilter);

    // Check if field contains any English characters
    const hasEnglishChar = /[a-zA-Z]/.test(value);

    if (hasEnglishChar) {
      // Close Urdu keyboard if English character is found
      setShowSearchUrduKeyboard(false);
    } else if (value.trim() === '' && showSearchUrduKeyboard === false) {
      // Open Urdu keyboard if field becomes empty and keyboard is not already open
      // Only open if the field is focused (to avoid opening when form is reset)
      if (searchInputRef.current && searchInputRef.current === document.activeElement) {
        setShowSearchUrduKeyboard(true);
      }
    }
  }, [supplierSearchFilter, showSearchUrduKeyboard]);

  // Filter transactions by date
  const filterTransactionsByDate = (transactions) => {
    if (!dateFrom && !dateTo) return transactions;

    return transactions.filter(tx => {
      const txDate = new Date(tx.date || tx.createdAt || 0);
      const fromDate = dateFrom ? new Date(dateFrom) : null;
      const toDate = dateTo ? new Date(dateTo) : null;

      if (fromDate && toDate) {
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999);
        return txDate >= fromDate && txDate <= toDate;
      } else if (fromDate) {
        fromDate.setHours(0, 0, 0, 0);
        return txDate >= fromDate;
      } else if (toDate) {
        toDate.setHours(23, 59, 59, 999);
        return txDate <= toDate;
      }
      return true;
    });
  };

  // Download PDF
  const handleDownloadPDF = async () => {
    if (!printRef.current) {
      toast.error(companyLanguage === 'ur' ? 'مواد نہیں ملا' : 'Content not found');
      return;
    }

    try {
      setIsGeneratingPDF(true);
      toast.info(companyLanguage === 'ur' ? 'PDF بنایا جا رہا ہے...' : 'Generating PDF...');

      const element = printRef.current;

      // Show print header and hide elements that should be hidden in PDF
      const printHeader = element.querySelector('[class*="print:block"]');
      const hiddenElements = element.querySelectorAll('[class*="print:hidden"]');

      // Show print header
      if (printHeader) {
        printHeader.style.display = 'block';
      }

      // Hide elements that should be hidden in print/PDF
      hiddenElements.forEach(el => {
        el.style.display = 'none';
      });

      // Add temporary styles for PDF
      const style = document.createElement('style');
      style.id = 'pdf-print-styles';
      style.textContent = `
        .bg-gradient-to-br { background: white !important; }
        .from-green-50 { background: white !important; }
        .via-emerald-50 { background: white !important; }
        .to-teal-50 { background: white !important; }
        .bg-white { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0.5cm !important; }
        .rounded-lg { border-radius: 0 !important; }
        .shadow-lg { box-shadow: none !important; }
        table { width: 100% !important; max-width: 100% !important; table-layout: auto !important; }
        .overflow-x-auto { width: 100% !important; overflow: visible !important; }
        div[ref] { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
      `;
      document.head.appendChild(style);

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      // Restore original display states
      if (printHeader) {
        printHeader.style.display = '';
      }
      hiddenElements.forEach(el => {
        el.style.display = '';
      });

      // Remove temporary styles
      const tempStyle = document.getElementById('pdf-print-styles');
      if (tempStyle) {
        document.head.removeChild(tempStyle);
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const partyName = selectedLedgerParty?.name || 'Supplier';
      const dateRange = dateFrom && dateTo
        ? `_${dateFrom}_to_${dateTo}`
        : dateFrom
          ? `_from_${dateFrom}`
          : dateTo
            ? `_to_${dateTo}`
            : '';
      const fileName = `Supplier_Khata_${partyName}${dateRange}_${new Date().toLocaleDateString('en-CA')}.pdf`;

      pdf.save(fileName);
      toast.success(companyLanguage === 'ur' ? 'PDF ڈاؤن لوڈ ہو گیا!' : 'PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(companyLanguage === 'ur' ? 'PDF بنانے میں ناکام' : 'Failed to generate PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Download Excel
  const handleDownloadExcel = () => {
    if (!selectedLedgerParty) {
      toast.error(companyLanguage === 'ur' ? 'کوئی پارٹی منتخب نہیں' : 'No party selected');
      return;
    }

    try {
      const filteredTransactions = filterTransactionsByDate(ledgerTransactions);

      const excelData = [];

      // Add header
      excelData.push([
        companyLanguage === 'ur' ? 'تاریخ' : 'Date',
        companyLanguage === 'ur' ? 'تفصیل' : 'Particulars',
        companyLanguage === 'ur' ? 'واؤچر نمبر' : 'Vch No.',
        companyLanguage === 'ur' ? 'سیل' : 'Sale',
        companyLanguage === 'ur' ? 'ریکوری' : 'Recovery',
        companyLanguage === 'ur' ? 'بیلنس' : 'Balance'
      ]);

      // Add opening balance
      excelData.push([
        '',
        companyLanguage === 'ur' ? 'ابتدائی بیلنس' : 'Opening Balance',
        '-',
        (() => {
          const opening = selectedLedgerParty.openingBalance || 0;
          const isPayable = selectedLedgerParty.balanceType === 'payable' || opening < 0;
          const amountAbs = Math.abs(opening);
          return !isPayable && amountAbs ? amountAbs : '';
        })(),
        '-',
        (() => {
          const opening = selectedLedgerParty.openingBalance || 0;
          const isPayable = selectedLedgerParty.balanceType === 'payable' || opening < 0;
          const amountAbs = Math.abs(opening);
          return isPayable ? -amountAbs : '';
        })()
      ]);

      // Group transactions by billId
      const groupedTransactions = {};
      filteredTransactions.forEach(tx => {
        let billIdValue = null;
        if (tx.billId) {
          if (typeof tx.billId === 'object' && tx.billId._id) {
            billIdValue = tx.billId._id.toString();
          } else if (typeof tx.billId === 'object') {
            billIdValue = tx.billId.toString();
          } else {
            billIdValue = tx.billId.toString();
          }
        }

        // Get vehicle/gardi number from populated bill (if available)
        const vehicleNumber =
          (tx.billId && typeof tx.billId === 'object'
            ? (tx.billId.expenses?.ActualVehicle ||
              tx.billId.expenses?.Vehicle ||
              tx.billId.expenses?.actualVehicle ||
              tx.billId.expenses?.vehicle ||
              '')
            : '') || '';

        const key = billIdValue || tx._id.toString();

        if (!groupedTransactions[key]) {
          let voucherNumber = '-';
          if (billIdValue) {
            voucherNumber = billIdValue.slice(-6);
          } else if (tx.billId && typeof tx.billId === 'object' && tx.billId.serialNumber) {
            voucherNumber = tx.billId.serialNumber;
          }

          groupedTransactions[key] = {
            date: tx.date,
            description: tx.description || '-',
            voucherNumber: voucherNumber,
            vehicleNumber: vehicleNumber,
            sale: 0,
            recovery: 0,
            balanceAfter: tx.balanceAfter || 0
          };
        } else if (!groupedTransactions[key].vehicleNumber && vehicleNumber) {
          // If later tx has vehicle number, keep it
          groupedTransactions[key].vehicleNumber = vehicleNumber;
        }

        if (tx.type === 'debit') {
          groupedTransactions[key].sale += tx.amount || 0;
        } else if (tx.type === 'credit') {
          groupedTransactions[key].recovery += tx.amount || 0;
        }

        if (tx.balanceAfter) {
          const txDate = new Date(tx.date || tx.createdAt || 0);
          const groupDate = new Date(groupedTransactions[key].date || 0);
          if (!groupedTransactions[key].balanceAfter || txDate >= groupDate) {
            groupedTransactions[key].balanceAfter = tx.balanceAfter;
            groupedTransactions[key].date = tx.date;
          }
        }
      });

      // Sort by date
      const sortedTransactions = Object.values(groupedTransactions).sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateA - dateB;
      });

      // Add transactions to excel data
      // Match table logic:
      // Receivable opening counts as Sale impact (reduces balance)
      // Payable opening counts as starting negative balance
      const openingRaw = selectedLedgerParty.openingBalance || 0;
      const openingAbs = Math.abs(openingRaw);
      const isPayableOpening = selectedLedgerParty.balanceType === 'payable' || openingRaw < 0;
      const openingAsSale = !isPayableOpening ? openingAbs : 0;
      const startingBalance = isPayableOpening ? -openingAbs : 0;
      let runningBalance = (startingBalance || 0) - (openingAsSale || 0);
      sortedTransactions.forEach(tx => {
        runningBalance = (runningBalance || 0) - (tx.sale || 0) + (tx.recovery || 0);
        excelData.push([
          new Date(tx.date).toLocaleDateString(),
          tx.description.replace(' (سیل)', '').replace(' (ریکوری)', '').replace(' (Sale)', '').replace(' (Recovery)', '') || '-',
          tx.vehicleNumber ? `${tx.voucherNumber} (${tx.vehicleNumber})` : tx.voucherNumber,
          tx.sale > 0 ? tx.sale : '',
          tx.recovery > 0 ? tx.recovery : '',
          runningBalance
        ]);
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Supplier Khata');

      // Generate filename
      const partyName = selectedLedgerParty.name || 'Supplier';
      const dateRange = dateFrom && dateTo
        ? `_${dateFrom}_to_${dateTo}`
        : dateFrom
          ? `_from_${dateFrom}`
          : dateTo
            ? `_to_${dateTo}`
            : '';
      const fileName = `Supplier_Khata_${partyName}${dateRange}_${new Date().toLocaleDateString('en-CA')}.xlsx`;

      XLSX.writeFile(wb, fileName);
      toast.success(companyLanguage === 'ur' ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : 'Excel file downloaded successfully');
    } catch (error) {
      console.error('Excel download error:', error);
      toast.error(companyLanguage === 'ur' ? 'Excel ڈاؤن لوڈ میں ناکام' : 'Failed to download Excel');
    }
  };

  // Print
  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      {/* PDF/Print Container */}
      <div ref={printRef} className="print:w-full print:max-w-full print:m-0 print:p-0">
        {/* Print Header - Company Info - Top of Page */}
        {selectedLedgerParty && (
          <div className="hidden print:block border-b-2 border-gray-400 pb-3 mb-4 bg-white p-4">
            <div className="flex items-start justify-between mb-2 gap-4">
              {/* Company Name - Top Left */}
              <div className="text-left flex-shrink-0">
                <h1
                  className={`text-xl font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                  style={{
                    ...(companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}),
                    color: '#1e40af',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}
                >
                  {company?.shopName || company?.username || company?.shopNameUrdu || 'Company Name'}
                </h1>
                {company?.username && (
                  <p className={`text-sm text-gray-600 font-medium mt-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                    {companyLanguage === 'ur' ? 'نام:' : 'Name:'} {company.username}
                  </p>
                )}
                {(company?.contactNumber || company?.phone || company?.mobile) && (
                  <p className="text-sm text-gray-700 font-semibold mt-1">
                    {companyLanguage === 'ur' ? 'فون:' : 'Phone:'} {company.contactNumber || company.phone || company.mobile}
                  </p>
                )}
              </div>

              {/* Logo - Top Right */}
              <div className="flex-shrink-0" style={{ alignSelf: 'flex-start' }}>
                {company?.logo && (
                  <img
                    src={company.logo}
                    alt="Logo"
                    className="h-16 w-16 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </div>
            </div>
            {company?.address && (
              <div className="mt-2 pt-2 border-t border-gray-300">
                <p className={`text-xs text-gray-700 text-center ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                  {company.address}
                </p>
              </div>
            )}
            {/* Supplier Khata Title */}
            <div className="mt-3 pt-2">
              <h2 className={`text-lg font-bold text-center ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                {companyLanguage === 'ur' ? 'سپلائر کھاتا' : 'Supplier Khata'}
              </h2>
            </div>
          </div>
        )}

        {/* Supplier Khata Tab */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Parties list */}
          <div className="md:col-span-1 bg-white rounded-lg shadow-lg p-4 border border-brand-card flex flex-col print:hidden">
            <div className={`flex items-center justify-between mb-4 ${companyLanguage === 'ur' ? 'flex-row-reverse' : ''}`}>
              <h3 className={`text-lg font-semibold text-brand-header ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {companyLanguage === 'ur' ? 'سپلائر' : 'Suppliers'}
              </h3>
            </div>

            {/* Search Filter */}
            <div className="mb-4">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={supplierSearchFilter}
                  onChange={(e) => setSupplierSearchFilter(e.target.value)}
                  onClick={handleSearchInputClick}
                  className={`w-full px-4 pl-10 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all ${companyLanguage === 'ur' ? 'font-urdu text-right' : ''}`}
                  style={companyLanguage === 'ur' ? {
                    fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                    lineHeight: '1.8',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    fontSize: '13px',
                    height: 'auto',
                    minHeight: '44px',
                    display: 'block',
                    overflow: 'visible',
                    verticalAlign: 'middle'
                  } : {}}
                  dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
                  lang={companyLanguage === 'ur' ? 'ur' : 'en'}
                  placeholder={companyLanguage === 'ur' ? 'سپلائر تلاش کریں...' : 'Search supplier...'}
                />
                <svg className={`absolute ${companyLanguage === 'ur' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Add Supplier Button */}
            <button
              onClick={() => {
                setEditingPartyId(null);
                setLedgerPartyForm({
                  name: '',
                  nameUrdu: '',
                  type: 'supplier',
                  phone: '',
                  address: '',
                  openingBalance: '',
                  balanceType: 'receivable'
                });
                setShowSupplierForm(true);
              }}
              className="mb-4 w-full bg-brand-header hover:bg-emerald-800 text-white font-semibold py-3 rounded-lg transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className={companyLanguage === 'ur' ? 'font-urdu' : ''} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {companyLanguage === 'ur' ? 'سپلائر شامل کریں' : 'Add Supplier'}
              </span>
            </button>

            {/* Create / Edit Party Form - Hidden by default */}
            {showSupplierForm && (
              <form ref={formRef} onSubmit={handleCreateLedgerParty} className="mb-4 space-y-3 p-4 bg-brand-background rounded-lg border border-brand-card">
                <div className={`flex items-center justify-between mb-2 ${companyLanguage === 'ur' ? 'flex-row-reverse' : ''}`}>
                  <h4 className={`text-sm font-semibold text-brand-header ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {editingPartyId ? (companyLanguage === 'ur' ? 'پارٹی تبدیل کریں' : 'Edit Party') : (companyLanguage === 'ur' ? 'نئی پارٹی بنائیں' : 'Create New Party')}
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSupplierForm(false);
                      setEditingPartyId(null);
                      setLedgerPartyForm({
                        name: '',
                        nameUrdu: '',
                        type: 'supplier',
                        phone: '',
                        address: '',
                        openingBalance: '',
                        balanceType: 'receivable'
                      });
                    }}
                    className="text-gray-500 hover:text-red-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <input
                      type="text"
                      name="name"
                      value={ledgerPartyForm.name}
                      onChange={handleLedgerPartyFormChange}
                      required
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm"
                      style={{ lineHeight: '1.7', paddingTop: '8px', paddingBottom: '8px', display: 'block', overflow: 'visible' }}
                      placeholder={companyLanguage === 'ur' ? 'نام (انگریزی)' : 'Name (English)'}
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      ref={urduNameInputRef}
                      type="text"
                      name="nameUrdu"
                      value={ledgerPartyForm.nameUrdu}
                      onChange={handleLedgerPartyFormChange}
                      onFocus={() => {
                        // Open Urdu keyboard on focus, especially if field is empty
                        if (!ledgerPartyForm.nameUrdu || ledgerPartyForm.nameUrdu.trim() === '') {
                          setShowUrduKeyboard(true);
                        }
                      }}
                      onClick={() => {
                        // Open Urdu keyboard on click if field is empty
                        if (!ledgerPartyForm.nameUrdu || ledgerPartyForm.nameUrdu.trim() === '') {
                          setShowUrduKeyboard(true);
                        }
                      }}
                      className={`w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", lineHeight: '1.7', paddingTop: '8px', paddingBottom: '8px', display: 'block', overflow: 'visible' } : { lineHeight: '1.7', paddingTop: '8px', paddingBottom: '8px', display: 'block', overflow: 'visible' }}
                      dir="rtl"
                      lang="ur"
                      inputMode="text"
                      autoComplete="off"
                      placeholder={companyLanguage === 'ur' ? 'نام (اردو)' : 'Name (Urdu)'}
                    />
                  </div>
                  <input
                    type="hidden"
                    name="type"
                    value="supplier"
                  />
                  <div>
                    <div className="w-full px-3 py-1.5 border-2 border-brand-card rounded-lg text-sm bg-brand-background text-gray-600">
                      {companyLanguage === 'ur' ? 'سپلائر' : 'Supplier'}
                    </div>
                  </div>
                  <div>
                    <input
                      type="number"
                      name="openingBalance"
                      value={ledgerPartyForm.openingBalance}
                      onChange={handleLedgerPartyFormChange}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm text-right"
                      placeholder={companyLanguage === 'ur' ? 'موجودہ بیلنس' : 'Current Balance'}
                    />
                  </div>
                  <div>
                    <select
                      name="balanceType"
                      value={ledgerPartyForm.balanceType}
                      onChange={handleLedgerPartyFormChange}
                      className={`w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    >
                      <option value="receivable">{companyLanguage === 'ur' ? 'قابل وصول' : 'Receivable'}</option>
                      <option value="payable">{companyLanguage === 'ur' ? 'قابل ادائیگی' : 'Payable'}</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input
                      type="text"
                      name="phone"
                      value={ledgerPartyForm.phone}
                      onChange={handleLedgerPartyFormChange}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm"
                      placeholder={companyLanguage === 'ur' ? 'فون' : 'Phone'}
                    />
                  </div>
                  <div className="col-span-2">
                    <textarea
                      name="address"
                      value={ledgerPartyForm.address}
                      onChange={handleLedgerPartyFormChange}
                      rows="2"
                      className={`w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm ${companyLanguage === 'ur' ? 'font-urdu text-right' : ''}`}
                      style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                      dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
                      placeholder={companyLanguage === 'ur' ? 'پتہ' : 'Address'}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSupplierForm(false);
                      setEditingPartyId(null);
                      setLedgerPartyForm({
                        name: '',
                        nameUrdu: '',
                        type: 'supplier',
                        phone: '',
                        address: '',
                        openingBalance: '',
                        balanceType: 'receivable'
                      });
                    }}
                    className="flex-1 px-3 py-1.5 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-semibold transition"
                  >
                    {companyLanguage === 'ur' ? 'منسوخ' : 'Cancel'}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-brand-header hover:bg-emerald-800 text-white text-sm font-semibold py-1.5 rounded-lg transition-all transform hover:scale-105"
                  >
                    {editingPartyId ? (companyLanguage === 'ur' ? 'اپ ڈیٹ کریں' : 'Update Party') : (companyLanguage === 'ur' ? 'محفوظ کریں' : 'Save Party')}
                  </button>
                </div>
              </form>
            )}

            {/* Supplier List */}
            <div className="flex-1 overflow-y-auto">
              {isLoadingLedger ? (
                <p className={`text-gray-500 text-sm text-center py-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
                </p>
              ) : (() => {
                const filteredParties = ledgerParties.filter(party => {
                  // Filter by supplier type first
                  if (party.type !== 'supplier') return false;
                  if (!supplierSearchFilter) return true;

                  const searchLower = supplierSearchFilter.toLowerCase();
                  return (
                    party.name?.toLowerCase().includes(searchLower) ||
                    party.nameUrdu?.toLowerCase().includes(searchLower) ||
                    party.nameUrdu?.includes(supplierSearchFilter) || // Also check without lowercase for exact Urdu match
                    party.phone?.includes(supplierSearchFilter)
                  );
                });

                if (filteredParties.length === 0) {
                  return (
                    <p className={`text-gray-500 text-sm text-center py-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {supplierSearchFilter ? (companyLanguage === 'ur' ? 'کوئی سپلائر نہیں ملا' : 'No supplier found') : (companyLanguage === 'ur' ? 'ابھی کوئی پارٹی نہیں' : 'No parties yet')}
                    </p>
                  );
                }

                return (
                  <ul className="divide-y divide-gray-200">
                    {filteredParties.map((party) => (
                      <li
                        key={party._id}
                        className={`py-2 px-2 rounded transition ${selectedLedgerParty && selectedLedgerParty._id === party._id
                            ? 'bg-brand-header text-white'
                            : 'hover:bg-brand-background'
                          }`}
                      >
                        <div
                          onClick={() => fetchLedgerTransactions(party._id)}
                          className="cursor-pointer"
                        >
                          <div className="flex justify-between items-center">
                            <span className={`font-semibold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {companyLanguage === 'ur' ? (party.nameUrdu || party.name) : party.name}
                            </span>
                            <span className="text-xs opacity-70">
                              {party.type === 'customer'
                                ? companyLanguage === 'ur' ? 'گاہک' : 'Customer'
                                : companyLanguage === 'ur' ? 'سپلائر' : 'Supplier'}
                            </span>
                          </div>
                          <p className={`text-xs mt-1 ${selectedLedgerParty && selectedLedgerParty._id === party._id ? 'text-white/80' : 'text-gray-500'}`}>
                            {companyLanguage === 'ur' ? 'بیلنس:' : 'Balance:'}{' '}
                            <span className={`font-semibold ${(party.balanceType === 'payable' || (party.currentBalance < 0))
                                ? 'text-red-600'
                                : ''
                              }`}>
                              {party.balanceType === 'payable' || (party.currentBalance < 0)
                                ? `-${Math.abs(party.currentBalance || 0).toLocaleString()}`
                                : (party.currentBalance?.toLocaleString() || 0)}
                            </span>
                            {parseFloat(party.currentBalance || 0) !== 0 && (
                              <span className="text-xs opacity-70 ml-1">
                                {party.balanceType === 'receivable' ? (companyLanguage === 'ur' ? 'قابل وصول' : 'Receivable') : (companyLanguage === 'ur' ? 'قابل ادائیگی' : 'Payable')}
                              </span>
                            )}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </div>

          {/* Transactions */}
          <div className="md:col-span-2 bg-white rounded-lg shadow-lg p-4 border border-brand-card print:col-span-full">
            {selectedLedgerParty ? (
              <>
                <div className={`flex justify-between items-center mb-4 ${companyLanguage === 'ur' ? 'flex-row-reverse' : ''}`}>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {companyLanguage === 'ur'
                        ? (selectedLedgerParty.nameUrdu || selectedLedgerParty.name)
                        : selectedLedgerParty.name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {companyLanguage === 'ur' ? 'موجودہ بیلنس:' : 'Current Balance:'}{' '}
                      <span className={`font-semibold ${(selectedLedgerParty.balanceType === 'payable' || (selectedLedgerParty.currentBalance < 0))
                          ? 'text-red-600'
                          : ''
                        }`}>
                        {selectedLedgerParty.balanceType === 'payable' || (selectedLedgerParty.currentBalance < 0)
                          ? `-${Math.abs(selectedLedgerParty.currentBalance || 0).toLocaleString()}`
                          : (selectedLedgerParty.currentBalance?.toLocaleString() || 0)}
                      </span>
                    </p>
                  </div>
                </div>
                {/* Manual "Add Transaction" form removed as requested */}

                {/* Date Filter and Action Buttons */}
                <div className={`mb-4 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between print:hidden ${companyLanguage === 'ur' ? 'flex-row-reverse' : ''}`}>
                  <div className="flex flex-col md:flex-row gap-2">
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded text-sm"
                      placeholder={companyLanguage === 'ur' ? 'تاریخ سے' : 'From Date'}
                    />
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded text-sm"
                      placeholder={companyLanguage === 'ur' ? 'تاریخ تک' : 'To Date'}
                    />
                    {(dateFrom || dateTo) && (
                      <button
                        onClick={() => {
                          setDateFrom('');
                          setDateTo('');
                        }}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50"
                      >
                        {companyLanguage === 'ur' ? 'کلیر' : 'Clear'}
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleDownloadPDF}
                      disabled={isGeneratingPDF}
                      className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {isGeneratingPDF ? (companyLanguage === 'ur' ? 'لوڈ ہو رہا ہے...' : 'Loading...') : (companyLanguage === 'ur' ? 'PDF ڈاؤن لوڈ' : 'Download PDF')}
                    </button>
                    <button
                      onClick={handleDownloadExcel}
                      className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {companyLanguage === 'ur' ? 'Excel ڈاؤن لوڈ' : 'Download Excel'}
                    </button>
                    <button
                      onClick={handlePrint}
                      className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      {companyLanguage === 'ur' ? 'پرنٹ' : 'Print'}
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  {(() => {
                    // Filter bills by selected supplier
                    const supplierBills = bills.filter(bill => {
                      if (!selectedLedgerParty) return false;

                      // Handle customerPartyId - it might be a string, object with _id, or populated object
                      let billSupplierId = null;
                      if (bill.customerPartyId) {
                        if (typeof bill.customerPartyId === 'object' && bill.customerPartyId._id) {
                          billSupplierId = bill.customerPartyId._id.toString();
                        } else if (typeof bill.customerPartyId === 'object') {
                          billSupplierId = bill.customerPartyId.toString();
                        } else {
                          billSupplierId = bill.customerPartyId.toString();
                        }
                      } else if (bill.supplierPartyId) {
                        if (typeof bill.supplierPartyId === 'object' && bill.supplierPartyId._id) {
                          billSupplierId = bill.supplierPartyId._id.toString();
                        } else if (typeof bill.supplierPartyId === 'object') {
                          billSupplierId = bill.supplierPartyId.toString();
                        } else {
                          billSupplierId = bill.supplierPartyId.toString();
                        }
                      }

                      const selectedPartyId = selectedLedgerParty._id.toString();

                      return billSupplierId && billSupplierId === selectedPartyId;
                    });

                    // Debug logging for filtered results
                    if (process.env.NODE_ENV === 'development') {
                      console.log('Supplier bills filtering:', {
                        totalBills: bills.length,
                        supplierBillsCount: supplierBills.length,
                        selectedSupplier: selectedLedgerParty?.name,
                        selectedSupplierId: selectedLedgerParty?._id,
                        sampleBill: bills.length > 0 ? {
                          billId: bills[0]._id,
                          customerPartyId: bills[0].customerPartyId,
                          customerName: bills[0].customerName
                        } : null
                      });
                    }

                    // Filter bills by date range
                    const filteredBills = filterTransactionsByDate(supplierBills.map(bill => ({
                      ...bill,
                      date: bill.date || bill.createdAt
                    })));

                    // Sort bills by date (oldest first)
                    const sortedBills = [...filteredBills].sort((a, b) => {
                      const dateA = new Date(a.date || a.createdAt || 0);
                      const dateB = new Date(b.date || b.createdAt || 0);
                      return dateA - dateB;
                    });

                    // Calculate totals
                    let totalKhambakri = 0;
                    let totalAkhrajat = 0;
                    let totalPukhtaBakri = 0;

                    sortedBills.forEach(bill => {
                      // Get raw sale (خام بکری) - same logic as BillView
                      const rawSale = bill.sales?.rawSale || bill.sales?.['Raw Sale'] || bill.expenses?.['خام بکری'] || bill.rawSale || 0;
                      const rawSaleNum = typeof rawSale === 'string' ? parseFloat(rawSale) || 0 : (rawSale || 0);

                      // Calculate total expenses - same logic as BillView (exclude non-expense fields)
                      let totalExpenses = 0;
                      if (bill.expenses && typeof bill.expenses === 'object') {
                        Object.keys(bill.expenses).forEach(key => {
                          const value = bill.expenses[key];
                          // Skip non-expense fields: Vehicle, ItemName, ItemUnit, ItemQuantity, ActualVehicle, calculated fields, and laga
                          const isNonExpenseField =
                            key === 'Vehicle' ||
                            key === 'ActualVehicle' ||
                            key === 'ItemName' ||
                            key === 'ItemUnit' ||
                            key === 'ItemQuantity' ||
                            key === 'خام بکری' ||
                            key === 'اخراجات' ||
                            key === 'پختہ بکری' ||
                            key === 'لاگا' || // Exclude laga from expenses (it goes to amdani only)
                            key === 'Laga' ||
                            key === 'DeliveryMethod' ||
                            key === 'GoodsMessageUrdu';

                          if (!isNonExpenseField) {
                            const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (typeof value === 'number' && !isNaN(value) ? value : 0);
                            totalExpenses += numValue;
                          }
                        });
                      }

                      // Calculate net sale (پختہ بکری) - same logic as BillView: rawSale - totalExpenses
                      // Don't use bill.expenses?.['پختہ بکری'] or bill.netSale as they might be incorrect
                      const netSale = rawSaleNum - totalExpenses;

                      totalKhambakri += rawSaleNum;
                      totalAkhrajat += totalExpenses;
                      totalPukhtaBakri += netSale;
                    });

                    return (
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-brand-background">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {companyLanguage === 'ur' ? 'خام بکری' : 'Raw Sale'}
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {companyLanguage === 'ur' ? 'اخراجات' : 'Expenses'}
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {companyLanguage === 'ur' ? 'پختہ بکری' : 'Net Sale'}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {sortedBills.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="px-3 py-4 text-center text-gray-500">
                                {companyLanguage === 'ur' ? 'کوئی بل نہیں' : 'No bills found'}
                              </td>
                            </tr>
                          ) : (
                            sortedBills.map((bill) => {
                              // Get raw sale (خام بکری) - same logic as BillView
                              const rawSale = bill.sales?.rawSale || bill.sales?.['Raw Sale'] || bill.expenses?.['خام بکری'] || bill.rawSale || 0;
                              const rawSaleNum = typeof rawSale === 'string' ? parseFloat(rawSale) || 0 : (rawSale || 0);

                              // Calculate total expenses - same logic as BillView (exclude non-expense fields)
                              let totalExpenses = 0;
                              if (bill.expenses && typeof bill.expenses === 'object') {
                                Object.keys(bill.expenses).forEach(key => {
                                  const value = bill.expenses[key];
                                  // Skip non-expense fields: Vehicle, ItemName, ItemUnit, ItemQuantity, ActualVehicle, and calculated fields
                                  const isNonExpenseField =
                                    key === 'Vehicle' ||
                                    key === 'ActualVehicle' ||
                                    key === 'ItemName' ||
                                    key === 'ItemUnit' ||
                                    key === 'ItemQuantity' ||
                                    key === 'خام بکری' ||
                                    key === 'اخراجات' ||
                                    key === 'پختہ بکری' ||
                                    key === 'DeliveryMethod' ||
                                    key === 'GoodsMessageUrdu';

                                  if (!isNonExpenseField) {
                                    const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (typeof value === 'number' && !isNaN(value) ? value : 0);
                                    totalExpenses += numValue;
                                  }
                                });
                              }

                              // Calculate net sale (پختہ بکری) - same logic as BillView: rawSale - totalExpenses
                              // Don't use bill.expenses?.['پختہ بکری'] or bill.netSale as they might be incorrect
                              const netSale = rawSaleNum - totalExpenses;

                              return (
                                <tr key={bill._id}>
                                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                    {bill.date ? new Date(bill.date).toLocaleDateString() : (bill.createdAt ? new Date(bill.createdAt).toLocaleDateString() : '-')}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">
                                    {rawSaleNum > 0 ? rawSaleNum.toLocaleString() : '-'}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">
                                    {totalExpenses > 0 ? totalExpenses.toLocaleString() : '-'}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700 font-semibold">
                                    {netSale !== 0 ? netSale.toLocaleString() : '-'}
                                  </td>
                                </tr>
                              );
                            })
                          )}

                          {/* Totals row */}
                          {sortedBills.length > 0 && (
                            <tr className="bg-brand-background">
                              <td className="px-3 py-2 text-gray-700 font-semibold">
                                {companyLanguage === 'ur' ? 'کل' : 'Total'}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-900 font-semibold">
                                {totalKhambakri.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-900 font-semibold">
                                {totalAkhrajat.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-900 font-semibold">
                                {totalPukhtaBakri.toLocaleString()}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-sm">
                {companyLanguage === 'ur' ? 'کوئی پارٹی منتخب نہیں' : 'No party selected'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:col-span-full {
            grid-column: 1 / -1 !important;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            margin: 0.5cm;
          }
          .grid {
            display: block !important;
          }
          .md\\:grid-cols-3 {
            display: block !important;
          }
          .bg-white {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0.5cm !important;
          }
          .rounded-lg {
            border-radius: 0 !important;
          }
          .shadow-lg {
            box-shadow: none !important;
          }
          table {
            width: 100% !important;
            max-width: 100% !important;
          }
          .overflow-x-auto {
            width: 100% !important;
            overflow: visible !important;
          }
          [ref] {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
      <UrduKeyboard
        isVisible={showUrduKeyboard}
        onKeyPress={handleUrduKeyPress}
        onClose={() => setShowUrduKeyboard(false)}
      />
      <UrduKeyboard
        isVisible={showSearchUrduKeyboard}
        onKeyPress={handleSearchUrduKeyPress}
        onClose={() => setShowSearchUrduKeyboard(false)}
      />
    </div>
  );
}

export default SupplierKhata;

